import { BaseSensorWebController } from '../../base/controller/BaseSensorWebController';
import { BaseSensorWebComponent } from '../../base/BaseSensorWebComponent';
import { GoogleWorkspaceSensorComponent } from '../component/GoogleWorkspaceSensorComponent';
import { GoogleWorkspaceSensorView } from '../view/GoogleWorkspaceSensorView';
import { templateGoogleWorkspaceSensor } from '../view/template';
import { IListenerConfig } from '../../../../framework/BaseWebComponentController';
import { GzInputFileWebComponent } from '../../../../ui/gzInputFile';
import { GzToastNotificationWebComponent } from '../../../../ui/gzToastNotification';
import { ToastSeverity } from '../../../../ui/gzToastNotification/type';

export class GoogleWorkspaceSensorController extends BaseSensorWebController {
  public component: BaseSensorWebComponent;
  public view: GoogleWorkspaceSensorView;
  public viewConstructor: typeof GoogleWorkspaceSensorView;

  constructor(component: GoogleWorkspaceSensorComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: GoogleWorkspaceSensorView,
      viewTemplate: templateGoogleWorkspaceSensor,
    });
  }

  public isSensorEnabled(): boolean {
    return true;
  }

  computeEventListeners(): IListenerConfig[] {
    return super.computeEventListeners().concat([
      {
        element: this.view.dom.$iss,
        event: 'change',
        callback: this.inputChanged,
      },
      {
        element: this.view.dom.$adminEmail,
        event: 'change',
        callback: this.inputChanged,
      },
      {
        element: this.view.dom.$domain,
        event: 'change',
        callback: this.inputChanged,
      },
      {
        element: this.view.dom.$privateKey,
        event: 'change',
        callback: this.inputChanged,
      },
      {
        element: this.view.dom.$serviceAccountDetails,
        event: 'change',
        callback: this.onFileInputChange,
      },
    ]);
  }

  public resetData(): void {
    super.resetData();
    this.view.dom.$iss.resetField();
    this.view.dom.$privateKey.resetField();
    this.view.dom.$adminEmail.resetField();
    this.view.dom.$domain.resetField();
  }

  public inputChanged(ev): void {
    super.inputChanged(ev);

    // reset "Import file" input value
    const fileInput = this.component.shadowRoot?.querySelector('gz-file-input') as GzInputFileWebComponent;
    fileInput.setFormValue(null);
  }

  private async onFileInputChange(): Promise<void> {
    const input = this.component.shadowRoot?.querySelector('gz-file-input') as GzInputFileWebComponent;
    if (!input) {
      return;
    }

    const file = input.getFormValue();
    if (!file) {
      return;
    }

    const serviceAccountFile = file[0] as File;

    // validate filename
    if (!serviceAccountFile.name) {
      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: this.dependencies.translateFn('Invalid filename'),
        severity: ToastSeverity.ERROR,
      });
      return;
    }

    // validate file type
    const allowedExtensions = /(\.json)$/i;
    if (!allowedExtensions.exec(serviceAccountFile.name)) {
      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: this.dependencies.translateFn('Unsupported file type. Select a .json file and try again'),
        severity: ToastSeverity.ERROR,
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = e => {
      if (e.target && e.target.result && typeof e.target.result === 'string') {
        let result = {};
        try {
          result = JSON.parse(e.target.result);
        } catch (e) {
          GzToastNotificationWebComponent.buildToastWrapperElement({
            message: this.dependencies.translateFn('Invalid JSON file'),
            severity: ToastSeverity.ERROR,
          });
          return;
        }

        if (!result.hasOwnProperty('private_key')) {
          GzToastNotificationWebComponent.buildToastWrapperElement({
            message: this.dependencies.translateFn(
              'No private key value detected in the uploaded file. Generate the file again and import it unmodified.'
            ),
            severity: ToastSeverity.ERROR,
          });
          return;
        }
        this.view.dom.$privateKey.value = result['private_key'];

        if (!result.hasOwnProperty('client_email')) {
          GzToastNotificationWebComponent.buildToastWrapperElement({
            message: this.dependencies.translateFn(
              'No client email value detected in the uploaded file. Generate the file again and import it unmodified.'
            ),
            severity: ToastSeverity.ERROR,
          });
          return;
        }
        this.view.dom.$iss.value = result['client_email'];
      }
    };

    reader.readAsText(serviceAccountFile);
  }
}
